import { useEffect, useRef, useState } from 'react';
import { DotLottieReact, setWasmUrl, type DotLottie } from '@lottiefiles/dotlottie-react';
import type { PropCelebrationKind } from '@/utils/propProgressCelebration';

setWasmUrl(`${import.meta.env.BASE_URL}lottie/dotlottie-player.wasm`);

const COMPLETE_FALLBACK_MS = 6_000;

const KIND_LABEL: Record<PropCelebrationKind, string> = {
  touchdown: 'Touchdown',
  rush: 'What a run',
  catch: 'What a catch',
  pass: 'What a throw',
};

type PropProgressCelebrationOverlayProps = {
  isOpen: boolean;
  kind: PropCelebrationKind | null;
  src: string | null;
  onComplete: () => void;
};

/**
 * Card-local scoring overlay. Plays a .lottie once, then yields to the progress bar.
 * Tap anywhere (or a load failure / timeout) skips to the bar fill.
 */
export default function PropProgressCelebrationOverlay({
  isOpen,
  kind,
  src,
  onComplete,
}: PropProgressCelebrationOverlayProps) {
  const [dotLottie, setDotLottie] = useState<DotLottie | null>(null);
  const completedRef = useRef(false);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const finish = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    onCompleteRef.current();
  };

  useEffect(() => {
    completedRef.current = false;
  }, [isOpen, kind, src]);

  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setTimeout(() => finish(), COMPLETE_FALLBACK_MS);
    return () => window.clearTimeout(timer);
  }, [isOpen, kind, src]);

  useEffect(() => {
    if (!dotLottie) return;
    const onDone = () => finish();
    dotLottie.addEventListener('complete', onDone);
    dotLottie.addEventListener('loadError', onDone);
    return () => {
      dotLottie.removeEventListener('complete', onDone);
      dotLottie.removeEventListener('loadError', onDone);
    };
  }, [dotLottie]);

  if (!isOpen || !kind || !src) return null;

  return (
    <div
      data-test-id="prop-progress-celebration"
      data-celebration-kind={kind}
      className="absolute inset-0 z-20 overflow-hidden rounded-[12px] bg-[#121212]"
      role="img"
      aria-label={KIND_LABEL[kind]}
    >
      <button
        type="button"
        data-test-id="prop-progress-celebration-skip"
        className="absolute inset-0 z-10 cursor-pointer"
        aria-label="Skip animation"
        onClick={finish}
      />
      <div
        data-test-id="prop-progress-celebration-animation"
        className="relative z-0 flex h-full w-full items-center justify-center"
        aria-hidden
      >
        <DotLottieReact
          src={src}
          autoplay
          loop={false}
          className="h-full w-full"
          dotLottieRefCallback={setDotLottie}
        />
      </div>
    </div>
  );
}
